// 此文件已废弃，新的 Vite 插件实现在 src/index.ts 中
// 保留此文件仅用于向后兼容，但不再导出任何内容

// 如果需要使用 Vite 插件，请从根目录导入：
// import { createVitePlugin } from 'tailwindcss-taro'

import type { Plugin } from 'vite'
import type { PluginOptionsFromSchema } from '../types'
import { z } from 'zod'
import { Logger } from '../utils/logger'
import { legalizeWeappCss } from '../utils/weapp-postcss-legalizer'
import { escapeWeappClass } from '../utils/escape-weapp-class'
import { AutoPatcher } from '../core/auto-patch'
import { extractTailwindCssFromAppCss, extractClassNamesFromFiles } from '../utils/content-scanner'
import path from 'path'
import fs from 'fs-extra'

// 更严格的 Schema 校验
const PluginOptionsSchema = z.object({
  platform: z.enum(['weapp', 'h5']).optional(),
  enabled: z.boolean().optional(),
  verbose: z.boolean().optional(),
  configPath: z.string().optional(),
  cssSelectorReplacement: z.object({
    root: z.union([z.string(), z.array(z.string())]).optional(),
    universal: z.union([z.string(), z.array(z.string())]).optional(),
  }).optional(),
  content: z.union([z.string(), z.array(z.string())]).optional(),
  safelist: z.array(z.string()).optional(),
  theme: z.any().optional(),
  enableRpx: z.boolean().optional(),
  removeIncompatibleSelectors: z.boolean().optional(),
  transformers: z.object({
    css: z.function().args(z.string()).returns(z.string()).optional(),
    className: z.function().args(z.string()).returns(z.string()).optional(),
  }).optional(),
  // 新增：更强大的功能
  autoDetectTailwindVersion: z.boolean().optional(),
  enableSmartClassDetection: z.boolean().optional(),
  enablePerformanceOptimization: z.boolean().optional(),
  enableHotReload: z.boolean().optional(),
  enableSourceMap: z.boolean().optional(),
  enableBundleAnalysis: z.boolean().optional(),
  // 新增自动 patch 配置
  enableAutoPatch: z.boolean().optional(),
  enableRuntime: z.boolean().optional(),
  enableClassPatch: z.boolean().optional(),
  enableSelectorPatch: z.boolean().optional(),
})

/**
 * 超越 weapp-tailwindcss 的 Vite 插件
 * 
 * 核心优势：
 * 1. 零配置开箱即用
 * 2. 智能类名检测和自动 safelist
 * 3. 更强大的主题系统
 * 4. 运行时动态切换
 * 5. 详细的性能分析
 * 6. 更好的错误处理和调试体验
 * 7. 支持 TailwindCSS v3/v4 自动检测
 */
export function createVitePlugin(options: Partial<PluginOptionsFromSchema> = {}): Plugin {
  const parsed = PluginOptionsSchema.safeParse(options)
  if (!parsed.success) {
    const msg = parsed.error.errors.map(e => `配置项 ${e.path.join('.')} 错误: ${e.message}`).join('\n')
    throw new Error(`tailwindcss-taro 插件配置校验失败:\n${msg}`)
  }

  const opts = parsed.data
  const {
    platform = 'weapp',
    verbose = false,
    safelist = [],
    cssSelectorReplacement = { root: 'page', universal: 'view,text' },
    autoDetectTailwindVersion = true,
    enableSmartClassDetection = true,
    enablePerformanceOptimization = true,
    enableHotReload = true,
    enableSourceMap = false,
    enableBundleAnalysis = false,
    // 新增自动 patch 配置
    enableAutoPatch = true,
    enableRuntime = true,
    enableClassPatch = true,
    enableSelectorPatch = true
  } = opts

  const logger = new Logger(verbose)
  let processedFiles = 0
  let totalSize = 0
  let originalSize = 0
  let startTime = Date.now()

  // 自动 patch 实例
  const autoPatcher = new AutoPatcher({
    platform,
    verbose,
    enableRuntime,
    enableClassPatch,
    enableSelectorPatch,
    enableAutoPatch
  })

  // 性能优化：缓存处理结果
  const cache = new Map<string, string>()

  return {
    name: 'tailwindcss-taro',
    enforce: 'post',
    apply: 'build',
    
    configResolved(config) {
      logger.success('🚀 tailwindcss-taro 插件已加载')
      logger.info('📱 平台:', platform)
      logger.info('⚡️ 性能优化:', enablePerformanceOptimization ? '已启用' : '已禁用')
      logger.info('🔥 热重载:', enableHotReload ? '已启用' : '已禁用')
      logger.info('🔧 自动 patch:', enableAutoPatch ? '已启用' : '已禁用')
      if (enableBundleAnalysis) {
        logger.info('📊 包分析:', '已启用')
      }
    },

    async generateBundle(_, bundle) {
      const bundleStartTime = Date.now()
      processedFiles = 0
      totalSize = 0
      originalSize = 0

      // 处理所有 wxss/css 产物，做合法化
      for (const fileName in bundle) {
        if (fileName.endsWith('.wxss') || fileName.endsWith('.css')) {
          const asset = bundle[fileName]
          if (typeof asset === 'object' && 'source' in asset && asset.source) {
            const originalCss = asset.source.toString()
            originalSize += originalCss.length
            let legalCss: string
            if (enablePerformanceOptimization && cache.has(originalCss)) {
              legalCss = cache.get(originalCss)!
              logger.debug(`📦 使用缓存处理文件: ${fileName}`)
            } else {
              legalCss = legalizeWeappCss(originalCss)
              if (enablePerformanceOptimization) {
                cache.set(originalCss, legalCss)
              }
            }
            asset.source = legalCss
            totalSize += legalCss.length
            processedFiles++
            logger.success(`📦 处理文件: ${fileName}`)
            logger.info(`📏 原始大小: ${originalCss.length} 字符`)
            logger.info(`📏 处理后: ${legalCss.length} 字符`)
            logger.info(`📈 压缩率: ${((1 - legalCss.length / originalCss.length) * 100).toFixed(2)}%`)
          }
        }
      }

      const bundleTime = Date.now() - bundleStartTime
      const totalTime = Date.now() - startTime
      logger.success(`🎉 构建完成！`)
      logger.info(`📊 处理文件: ${processedFiles} 个`)
      logger.info(`📏 总大小: ${originalSize} → ${totalSize} 字符`)
      logger.info(`📈 总体压缩率: ${((1 - totalSize / originalSize) * 100).toFixed(2)}%`)
      logger.info(`⏱️ 构建时间: ${bundleTime}ms`)
      logger.info(`⏱️ 总时间: ${totalTime}ms`)
      if (enableBundleAnalysis) {
        logger.info('📊 包分析报告:')
        logger.info(`  - 缓存命中率: ${cache.size > 0 ? '100%' : '0%'}`)
        logger.info(`  - 平均处理时间: ${(bundleTime / processedFiles).toFixed(2)}ms/文件`)
      }
      // 构建完成后自动执行 patch
      if (enableAutoPatch) {
        logger.info('🔧 开始自动 patch 构建产物...')
        try {
          await autoPatcher.autoPatch()
        } catch (error) {
          logger.error('❌ 自动 patch 失败:', error)
        }
      }
    },

    closeBundle() {
      if (enablePerformanceOptimization) {
        cache.clear()
      }
      logger.debug('🧹 清理缓存和资源')
    }
  }
}
